<template>
    <component
        :class="[$style['mds-tag'], sizeClass, classObject]"
        :is="componentType"
        v-bind="routingProps"
        :href="href"
        :data-mds-version="version"
    >
        <div :class="$style['mds-tag__text']">
            <slot>{{ text }}</slot>
        </div>
        <mds-button
            v-if="removable && !href"
            type="button"
            :class="[$style['mds-tag__remove-button']]"
            icon="remove"
            variation="icon-only"
            size="small"
            :onDark="onDark || onBlack"
            :text="removableAriaLabel"
            @keyup.enter="$emit('mds-tag-removed', $event.target, $event)"
            @click="$emit('mds-tag-removed', $event.target, $event)"
        ></mds-button>
    </component>
</template>

<script>
    import { MdsButton } from '@mds/button';
    import packageInfo from '../package.json';
    import MdsRoutable from '@mds/routable';

    const { name, version } = packageInfo;

    export default {
        name: 'MdsTag',
        mixins: [MdsRoutable],
        components: {
            MdsButton,
        },
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            href: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'When provided, makes the entire tag a link and sets the `href` path.',
                },
            },
            onBlack: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-black styling.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            onLight: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-light styling.',
                },
            },
            removable: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, adds an icon-only remove button that allows the tag to be removed.',
                },
            },
            removeIconAriaLabel: {
                type: String,
                default: 'Dismiss',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` attribute on the icon-only remove button.',
                },
            },
            size: {
                type: String,
                default: 'small',
                validator(value) {
                    return ['small', 'medium'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`.',
                    description: 'Sets the size of the tag.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'The text that appears in the tag. Can also be passed via the slots.',
                },
            },
        },
        computed: {
            componentType() {
                return this.isRoutable ? this.linkTag : this.href ? 'a' : 'div';
            },
            sizeClass() {
                return this.$style[`mds-tag--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-tag--link']] = this.href || this.isRoutable;
                obj[this.$style['mds-tag--on-light']] = this.onLight;
                obj[this.$style['mds-tag--on-dark']] = this.onDark;
                obj[this.$style['mds-tag--on-black']] = this.onBlack;

                return obj;
            },
            removableAriaLabel() {
                let tagText = '';

                if (this.text) {
                    tagText = this.text;
                }
                if (this.$slots.default) {
                    tagText = this.$slots.default[0].text;
                }

                return `${this.removeIconAriaLabel} ${tagText}`;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './tag.scss';
</style>
